import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AppShell } from "../lib/shell.js";

export type ThemeMode = "light" | "dark";

const THEME_KEY = "aam_theme";

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  shell: AppShell;
  setShell: (shell: AppShell) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme);
  const [shell, setShell] = useState<AppShell>("staff");

  const setTheme = useCallback((next: ThemeMode) => {
    setThemeState(next);
    localStorage.setItem(THEME_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute("data-shell", shell);
  }, [shell]);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, shell, setShell }),
    [theme, setTheme, toggleTheme, shell]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

/** Apply theme on public routes (form, login, marketing) before auth shell mounts. */
export function PublicShell({ shell, children }: { shell: AppShell; children: ReactNode }) {
  const { setShell } = useTheme();

  useEffect(() => {
    setShell(shell);
  }, [shell, setShell]);

  return <>{children}</>;
}
